(() => {
  "use strict";

  const listLimit = 10;
  const clearWishlistModal = new theme.Modals(
    "WishlistModal",
    "wishlist-modal"
  );

  const html = (strings, ...values) =>
    String.raw({ raw: strings }, ...values.map((v) => String(v)));

  class ConfirmPopover extends HTMLElement {
    connectedCallback() {
      this.closeBtn = this.querySelector(".js-close");
      this.confirmBtn = this.querySelector(".js-confirm");
      this.closeHandler = this.close.bind(this);
      this.confirmHandler = this.confirm.bind(this);
      this.closeBtn.addEventListener("click", this.closeHandler);
      this.confirmBtn.addEventListener("click", this.confirmHandler);
      this.isOpen = false;
    }
    disconnectedCallback() {
      this.closeBtn.removeEventListener("click", this.closeHandler);
      this.confirmBtn.removeEventListener("click", this.confirmHandler);
    }
    open(source, confirmCallback) {
      this.confirmCallback = confirmCallback;
      this.activeSource = source;
      this.activeSource.setAttribute("aria-expanded", true);

      this.classList.add("open");
      const rect = source.getBoundingClientRect();
      const wts = this.offsetWidth - rect.width;
      this.style.top = `${rect.bottom + window.scrollY + 4}px`;
      this.style.left = `${rect.left - wts}px`;
      this.style.right = "auto";
      this.isOpen = true;

      setTimeout(() => {
        theme.a11y.trapFocus({
          container: this,
          elementToFocus: this,
          namespace: "confirm-popover_focus",
        });
      }, 100);
      this.bindOpenEvents();
    }

    close(evt) {
      if (!this.isOpen) return;
      if (
        evt &&
        !evt.target.closest(".js-close") &&
        evt.target.closest("confirm-popover")
      )
        return;

      this.classList.remove("open");
      this.style = "";
      if (
        this.activeSource &&
        this.activeSource.getAttribute("aria-expanded")
      ) {
        this.activeSource.setAttribute("aria-expanded", "false");
        this.activeSource.focus();
      }
      this.isOpen = false;
      theme.a11y.removeTrapFocus({
        container: this,
        namespace: "confirm-popover_focus",
      });
      this.unbindOpenEvents();
    }
    async confirm(evt) {
      evt?.stopPropagation();
      if (this.confirmCallback) {
        this.confirmBtn.disabled = true;
        this.confirmBtn.classList.add("btn--loading");
        const result = this.confirmCallback();
        if (result instanceof Promise) {
          await result;
        }
        this.confirmBtn.disabled = false;
        this.confirmBtn.classList.remove("btn--loading");
      }
      this.close();
    }
    bindOpenEvents() {
      window.on(
        "keyup.cpopover",
        function (evt) {
          if (evt.keyCode === 27) {
            this.close();
          }
        }.bind(this)
      );
      window.on(
        "resize.cpopover",
        theme.utils.debounce(
          100,
          function () {
            this.close();
          }.bind(this)
        )
      );
      document.documentElement.on("click.cpopover", this.close.bind(this));
    }
    unbindOpenEvents() {
      document.documentElement.off(".cpopover");
      window.off("keyup.cpopover");
      window.off("resize.cpopover");
    }
  }
  customElements.define("confirm-popover", ConfirmPopover);

  class ItemList {
    constructor(container, items, type, emptyMarkup) {
      this.container = container;
      this.items = items;
      this.type = type;

      this.loadMoreBtn = container.querySelector(".load-more-btn");
      this.clearListBtn = container.querySelector(".clear-list-btn");
      this.countEl = container.querySelector(".item-count");
      this.itemsEl = container.querySelector(".wishlist-items");
      this.totalEl = container.querySelector(".wishlist-total");
      this.emptyMarkup = emptyMarkup;
      this.listLimit = 10;

      if (items.length === 0) {
        this.clearDisplay();
      }

      if (items.length > 0) this.init();
    }

    init() {
      this.totalPrice = 0;
      this.currencyCode = this.items[0].price.full.currencyCode;
      this.items.forEach((item, idx) => {
        this.totalPrice +=
          Number(
            item.price.discount
              ? item.price.discount.amount
              : item.price.full.amount
          ) ?? 0;
        if (idx < this.listLimit) {
          this.itemsEl.appendChild(item.itemEl);
        }
      });
      this.totalEl.innerHTML = `Total: ${formatMoney({
        amount: this.totalPrice,
        currencyCode: this.currencyCode,
      })}`;
      this.countEl.innerHTML = `${this.items.length} ${
        this.type === "variant" ? "Item" : "Product"
      }${this.items.length === 1 ? "" : "s"}`;

      this.clearListBtn.addEventListener("click", this.onClear.bind(this));
      this.clearListBtn.disabled = false;

      if (this.items.length > this.listLimit) {
        this.loadMoreBtn?.addEventListener("click", this.loadMore.bind(this));
        this.loadMoreBtn?.classList.remove("hide");
      }

      this.itemsEl.classList.remove("unload");
      this.container.classList.remove("hide");
    }

    removeItem(itemKey) {
      const itemIdx = this.items.findIndex((i) => i.itemKey === itemKey);
      if (itemIdx < 0) return;

      const toRemove = this.items.splice(itemIdx, 1)[0];
      toRemove.itemEl.remove();

      if (this.items.length === 0) {
        this.clearDisplay();
      } else {
        const itemPrice = Number(
          toRemove.price.discount
            ? toRemove.price.discount.amount
            : toRemove.price.full.amount
        );
        this.totalPrice -= itemPrice;
        this.totalEl.innerHTML = `Total: ${formatMoney({
          amount: this.totalPrice,
          currencyCode: this.currencyCode,
        })}`;
        this.countEl.innerHTML = `${this.items.length} ${
          this.type === "variant" ? "Item" : "Product"
        }${this.items.length === 1 ? "" : "s"}`;
      }
    }

    onClear() {
      clearWishlistModal.open();
      const confirmClearBtn =
        clearWishlistModal.modal.querySelector(".js-modal-clear");

      confirmClearBtn.addEventListener("click", async () => {
        confirmClearBtn.classList.add("btn--loading");
        const result = await theme.jk.Wishlist.clear(
          this.items.map((i) => i.itemKey)
        );
        confirmClearBtn.classList.remove("btn--loading");
        if (result) {
          clearWishlistModal.close();
          this.clearDisplay();
        }
      });
    }

    clearDisplay() {
      if (this.type === "variant") {
        this.clearListBtn.disabled = true;
        this.loadMoreBtn?.classList.add("hide");
        this.countEl.innerHTML = "0 Items";
        this.totalEl.innerHTML = "";
        this.itemsEl.innerHTML = this.emptyMarkup;
        this.itemsEl.classList.remove("unload");
      } else {
        this.container.classList.add("hide");
        this.container.innerHTML = "";
      }
    }

    loadMore() {
      const newLimit = Math.min(this.listLimit + listLimit, this.items.length);
      for (let i = this.listLimit; i < newLimit; i++) {
        const itemToAdd = this.items[i]?.itemEl;
        itemToAdd && this.itemsEl.appendChild(itemToAdd);
      }
      if (newLimit === this.items.length) this.loadMoreBtn?.remove();
      this.listLimit = newLimit;
      AOS && AOS.refreshHard();
    }
  }

  class JKWishlist extends HTMLElement {
    connectedCallback() {
      this.signedIn = this.hasAttribute("data-customer");
      this.mainListEl = this.querySelector("#mainList");
      this.savedListEl = this.querySelector("#savedList");
      this.itemCountEl = this.mainListEl.querySelector(".item-count");
      this.itemsWrapper = this.mainListEl.querySelector(".wishlist-items");
      this.markup = this.querySelector("template");

      this.confirmPopover = this.querySelector("confirm-popover");
      this.init();
    }

    init() {
      if (theme.jk.Wishlist.items.length === 0) {
        this.listEmpty();
      } else {
        this.buildItems();
      }
    }

    listEmpty() {
      this.itemsWrapper.innerHTML = this.markup.content.children[0].outerHTML;
      this.itemsWrapper.classList.remove("unload");
      this.itemCountEl.innerHTML = "0 Items";
    }

    async buildItems() {
      try {
        const response = await fetch("/apps/jkcustom/wishlist", {
          method: "POST",
          body: JSON.stringify({ items: theme.jk.Wishlist.items }),
          headers: { "Content-Type": "application/json" },
        });
        const { data } = await response.json();
        if (response.status === 200) {
          const [wishlist, saved] = data?.reduce(
            (acc, item) => {
              const mapped = this.mapItem(item);
              if (mapped && mapped.isVariant) {
                acc[0].push(mapped);
              } else if (mapped) {
                acc[1].push(mapped);
              }
              return acc;
            },
            [[], []]
          );
          this.wishlistList = new ItemList(
            this.mainListEl,
            wishlist,
            "variant",
            this.markup.content.children[0].outerHTML
          );
          this.savedList = new ItemList(this.savedListEl, saved, "product");

          AOS && AOS.refreshHard();
        } else {
          throw Error("error");
        }
      } catch (err) {
        this.itemsWrapper.innerHTML = this.markup.content.children[1].outerHTML;
        this.itemsWrapper.classList.remove("unload");
      }
    }

    mapItem(item) {
      if (!item) return null;
      const isVariant = item.id.includes("ProductVariant");
      const product = isVariant ? item.product : item;
      const collections = product.collections.nodes.map((c) => ({
        id: c.id.split("/").pop(),
        handle: c.handle,
      }));
      const itemId = item.id.split("/").pop();
      const itemKey = isVariant
        ? `${product.id.split("/").pop()}:${itemId}`
        : `${itemId}:`;
      const url = `/products/${product.handle}${
        isVariant ? "?variant=" + itemId : ""
      }`;

      const image = isVariant ? item.image : item.featuredImage;
      const imageMarkup = theme.buildProductImage(
        {
          title: isVariant ? item.product.title : item.title,
          image_responsive_url: theme.Images.buildImagePath(image.url),
          image_responsive_urls: theme.Images.buildImagePath(
            image.url[(180, 360, 540)]
          ),
          image_aspect_ratio: 100,
        },
        "150px"
      );

      const price = isVariant ? item.price : item.priceRange.maxVariantPrice;
      const discount = getDiscountPrice(collections, price);
      const priceMarkup = discount
        ? `<span class="grid-product__price--original">
                    ${formatMoney(price)}
                    </span><span>${formatMoney(discount)}</span>`
        : formatMoney(price);

      const variantDetails = isVariant
        ? getVariantDetails(item, collections)
        : null;

      const addLink =
        !isVariant ||
        product.tags.some((t) => t === "bold" || t === "Safety Toe") ||
        (product.tags.includes("leadtime") && item.quantityAvailable < 1) ||
        variantDetails?.inventory === "Sold out"
          ? `<a href="${url}" class="btn btn--small">See details</a>`
          : isVariant
          ? `<button type="button" class="add-to-cart btn btn--small" ${
              !item.availableForSale ? "disabled" : ""
            }>${item.availableForSale ? "Add to cart" : "Sold out"}</button>`
          : "";

      const itemEl = document.createElement("div");
      itemEl.className = "wishlist-item";
      itemEl.innerHTML = html`
        <a href="${url}" class="item-image">
          ${variantDetails?.inventory === "Sold out"
            ? `<div class="grid-product__tag grid-product__tag--sold-out">Sold Out</div>`
            : ""}
          ${imageMarkup}</a
        >
        <div class="item-details">
          <div>
            <a class="product-title" href="${url}">${product.title}</a>
            ${variantDetails?.element || ""}
            <div class="item-price" data-amount="${price.amount}">
              ${priceMarkup}
            </div>
          </div>
        </div>
        <div class="item-actions">
          ${addLink}
          <button
            class="item-remove btn btn--secondary btn--small"
            type="button"
            title="Remove"
            aria-label="Remove"
          >
            ${this.markup.content.children[2].outerHTML}
            <span>Remove</span>
          </button>
        </div>
      `;

      const addToCartBtn = itemEl.querySelector(".add-to-cart");
      addToCartBtn?.addEventListener("click", async (evt) => {
        addToCartBtn.classList.add("btn--loading");
        const properties = getVariantProperties(item, collections);
        const result = await addToCart([
          {
            id: itemId,
            quantity: 1,
            properties,
          },
        ]);
        addToCartBtn.classList.remove("btn--loading");
      });

      const removeBtn = itemEl.querySelector(".item-remove");

      removeBtn?.addEventListener("click", async (evt) => {
        evt.preventDefault();
        evt.stopPropagation();
        this.confirmPopover.open(removeBtn, () => {
          const removed = theme.jk.Wishlist.remove(itemKey);
          if (removed) {
            if (isVariant) {
              this.wishlistList.removeItem(itemKey);
            } else {
              this.savedList.removeItem(itemKey);
            }
          }
        });
      });

      return {
        item,
        itemEl,
        itemKey,
        isVariant,
        price: { full: price, discount },
      };
    }
  }
  customElements.define("jk-wishlist", JKWishlist);

  function getVariantProperties(variant, collections) {
    const timelines = theme.jk.settings.timelines;
    const properties = { _wishlist_item: 1 };
    if (
      variant.product.handle === "zipper" ||
      collections.some((c) => c.handle === "heavy-duty-belts")
    ) {
      if (variant.quantityAvailable > 0) {
        properties["Inventory Status"] = "In Stock";
      } else {
        properties["Manufacturing Timeline"] = "1 - 2 Weeks";
      }
    } else if (variant.product.tags.includes("leadtime")) {
      properties["Inventory Status"] =
        variant.quantityAvailable > 0 ? "In Stock" : "Needs Manufactured";
      if (variant.quantityAvailable < 1) {
        properties["Manufacturing Timeline"] =
          timelines.normalTimeline + " Weeks";
      }
    } else if (
      collections.some(
        (c) => c.handle === "stocked-boots" || c.handle === "non-stocked-boots"
      )
    ) {
      properties["Inventory Status"] =
        variant.quantityAvailable > 0 ? "In Stock" : "In Production";
      if (variant.quantityAvailable < 1) {
        properties["Manufacturing Timeline"] = `${
          timelines.stockExtended ? timelines.stockTimeline : "8 - 10"
        } Weeks`;
      } else {
        properties["_Manufacturing Timeline"] = "None";
      }
    } else if (collections.some((c) => c.handle === "bear-slayer-re-release")) {
      if (variant.quantityAvailable > 0) {
        properties["Inventory Status"] = "In Stock";
      } else {
        properties["Manufacturing Timeline"] = "10 - 14 Weeks";
      }
    }
    return properties;
  }

  function getVariantDetails(variant, collections) {
    const inventoryText =
      variant.availableForSale && variant.quantityAvailable > 0
        ? "In Stock"
        : variant.availableForSale && variant.quantityAvailable < 1
        ? "In production"
        : "Sold out";
    let variantName = html`<div class="variant-title">
      ${variant.title !== "Default Title" ? variant.title : ""}
    </div>`;

    const showInventory = collections.some(
      (c) =>
        c.handle === "stocked-boots" ||
        c.handle === "non-stocked-boots" ||
        c.handle === "heavy-duty-belts"
    );

    return {
      inventory: inventoryText,
      element:
        inventoryText === "Sold out" || !showInventory
          ? variantName
          : (variantName += html`<div class="inventory-data">
              <span
                class="icon-and-text ${inventoryText === "In production"
                  ? "inventory--leadtime"
                  : ""}"
              >
                <span class="icon icon--inventory"></span>
                <span>${inventoryText}</span>
              </span>
            </div>`),
    };
  }

  function getDiscountPrice(collections, money) {
    if (!collections || collections.length === 0) return null;
    const sale = sdcmData?.find((sd) =>
      collections.some((c) => c.id === sd.id)
    );
    if (!sale) return;
    const currencyCode = money.currencyCode;
    const amount = Number(money.amount);
    return {
      amount:
        sale.type === "percentage"
          ? amount - amount * (sale.amount * 0.01)
          : amount - sale.amount,
      savings: sale.amount,
      type: sale.type,
      currencyCode,
    };
  }

  function formatMoney({ amount, currencyCode }) {
    const convert = Shopify.currency.active === currencyCode;
    return theme.Currency.formatMoney(
      (convert ? Math.ceil(amount * Number(Shopify.currency.rate)) : amount) *
        100
    );
  }

  async function addToCart(items) {
    try {
      const response = await fetch(theme.routes.cartAdd, {
        method: "POST",
        body: JSON.stringify({ items }),
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
      });
      const data = await response.json();
      if (data.status === 422) {
        // console.error(data);
      } else {
        document.dispatchEvent(new CustomEvent("ajaxProduct:added"));
        return "success";
      }
    } catch (err) {
      // console.error(err);
    }
    return "error";
  }
})();
